import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { NewAlbumDialog } from "./NewAlbumDialog";

// Uploads/imports go into an album. Non-admins must pick one; admins may skip (-> Unsorted).
// The last-used destination is remembered server-side and pre-selected ("Add to: … ▾ Change").
export const AlbumChoice = ({ value, onChange, onSpace, newSpace, testid = "album-choice" }) => {
  const { user } = useAuth();
  const admin = user?.role === "admin";
  const [albums, setAlbums] = useState([]);
  const [creating, setCreating] = useState(false);
  const [remembered, setRemembered] = useState(null);
  const [editing, setEditing] = useState(false);
  const load = () => api.get("/albums", { params: { space: "all", kind: "regular", limit: 200 } })
    .then((r) => setAlbums(r.data.albums.filter((a) => !a.unsorted && !a.yearbook && !a.smart && (admin || ["private", "family", "guest"].includes(a.space)))));
  useEffect(() => {
    const a = value && albums.find((x) => x.id === value);
    if (a) onSpace?.(a.space);
  }, [value, albums]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    load();
    api.get("/upload-pref").then(({ data }) => {
      if (!data.valid) return setEditing(true);
      setRemembered(data);
      onChange(data.album_id);
      if (!data.album_id && data.space) onSpace?.(data.space);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const pick = (v) => (v === "__new" ? setCreating(true) : onChange(v || null));
  const free = admin || newSpace === "private";  // family may skip the album only for Private (-> their own Unsorted)
  const name = value ? albums.find((a) => a.id === value)?.name || remembered?.album_name : free ? "Unsorted" : null;
  return (
    <div className="filter-field mt-5 block" data-testid={testid}>
      {!editing && name ? (
        <div className="flex items-center gap-3" data-testid={`${testid}-remembered`}>
          <span className="text-sm text-neutral-800">Add to: <b data-testid={`${testid}-name`}>{name}</b> <ChevronDown size={13} className="inline" /></span>
          <button type="button" className="auth-link text-xs" onClick={() => setEditing(true)} data-testid={`${testid}-change`}>Change</button>
        </div>
      ) : (
        <label className="block">
          <span>Save into album{free ? " (optional)" : ""}</span>
          <select value={value || ""} onChange={(e) => pick(e.target.value)} data-testid={`${testid}-select`}>
            <option value="">{free ? "No album (goes to your Unsorted)" : "Choose an album…"}</option>
            {albums.map((a) => <option key={a.id} value={a.id}>{a.name} · {a.space === "private" ? "Private" : a.space[0].toUpperCase() + a.space.slice(1)}</option>)}
            <option value="__new">+ New album…</option>
          </select>
        </label>
      )}
      {!free && !value && <p className="text-xs text-neutral-500 mt-1" data-testid={`${testid}-hint`}>Pick an album or create one; photos are saved with that album's visibility.</p>}
      <NewAlbumDialog open={creating} defaultSpace={newSpace} onClose={() => setCreating(false)} onCreated={(a) => { load(); onChange(a.id); }} />
    </div>
  );
};
